import axios, {AxiosResponse} from 'axios'
import {PaginatedReportResult, REPORT_PAGE_SIZE} from '../inventory/components/reportTypes'

const API_URL = process.env.REACT_APP_THEME_API_URL

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface WarehouseOption {
  warehouseId: number
  warehouseName: string
}

export interface ProductOption {
  productId: number
  productName: string
}

export interface InventoryAgeingRecord {
  productName: string
  skuCode: string
  unit: string
  d0To30: number
  d31To60: number
  d61To90: number
  d90Plus: number
}

// ─── Options APIs ─────────────────────────────────────────────────────────────

export const getWarehouseOptions = async (
  companyId: string
): Promise<WarehouseOption[] | undefined> => {
  return await axios
    .get(`${API_URL}/warehouse/list/${companyId}`)
    .then((response: AxiosResponse<any[]>) => response.data)
    .then((data: any[]) =>
      data.map((w) => ({
        warehouseId: w.warehouse_id,
        warehouseName: w.warehouse_name,
      }))
    )
}

export const getProductOptions = async (
  companyId: string
): Promise<ProductOption[] | undefined> => {
  return await axios
    .get(`${API_URL}/product-catalogue/list/${companyId}`)
    .then((response: AxiosResponse<any[]>) => response.data)
    .then((data: any[]) =>
      data.map((p) => ({
        productId: p.product_id,
        productName: p.product_name,
      }))
    )
}

// ─── Report API ───────────────────────────────────────────────────────────────

export interface InventoryAgeingReportParams {
  company_id: number
  fy_name: string
  from_date: string
  to_date: string
  warehouse_ids: number[] | null
  product_ids: number[] | null
  product_line: string
  movement_days: number
  page: number
  page_size: number
}

export const getInventoryAgeingReport = async (
  params: InventoryAgeingReportParams
): Promise<PaginatedReportResult<InventoryAgeingRecord> | undefined> => {
  try {
    const response = await axios.post(`${API_URL}/inventory/report/inventory-ageing`, params)
    return {
      records: (response.data.records || []).map((s: any) => ({
        productName: s.product_name,
        skuCode: s.sku,
        unit: s.uom,
        d0To30: s.bckt_0_30_days ?? 0,
        d31To60: s.bckt_31_60_days ?? 0,
        d61To90: s.bckt_61_90_days ?? 0,
        d90Plus: s.bckt_90_plus_days ?? 0,
      })),
      page: response.data.page ?? params.page,
      pageSize: response.data.page_size ?? params.page_size ?? REPORT_PAGE_SIZE,
      totalRecords: response.data.total_records ?? 0,
      totalPages: response.data.total_pages ?? 1,
    }
  } catch (error: any) {
    if (error.response) {
      throw error.response.data
    }
  }
}
